import {
    AutocompleteInput,
    DataTable,
    DateInput,
    FormToolbar,
    NumberInput,
    ReferenceManyField,
    SaveButton,
    SelectInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { CircleX, PieChart, Plus } from 'lucide-react';
import { useGetList, useGetManyReference, useRecordContext, CreateBase } from 'ra-core';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { CostEntry } from '../../types';

const CATEGORIES = [
    { id: 'material', name: 'Material' },
    { id: 'labor', name: 'Labor' },
    { id: 'overhead', name: 'Overhead' },
    { id: 'freight', name: 'Freight' },
    { id: 'other', name: 'Other' },
    { id: 'custom', name: '+ Custom Category' },
];

const CustomCategoryField = () => {
    const category = useWatch({ name: 'category' });

    // Pull custom categories from ALL projects so a category created once is
    // suggested everywhere. New names are added inline via onCreate. (Cost #12)
    const { data: existingEntries } = useGetList('cost_entries', {
        pagination: { page: 1, perPage: 500 },
        filter: { category: 'custom' },
    }, { enabled: category === 'custom' });

    const customChoices = useMemo(() => {
        const seen = new Set<string>();
        return (existingEntries ?? []).reduce<{ id: string; name: string }[]>((acc, e) => {
            if (e.custom_category && !seen.has(e.custom_category)) {
                seen.add(e.custom_category);
                acc.push({ id: e.custom_category, name: e.custom_category });
            }
            return acc;
        }, []);
    }, [existingEntries]);

    if (category !== 'custom') return null;

    return (
        <AutocompleteInput
            source="custom_category"
            label="Custom Category Name"
            choices={customChoices}
            onCreate={(value) => ({ id: value ?? '', name: value ?? '' })}
            isRequired
            className="w-full"
            placeholder="e.g. Testing, Logistics..."
        />
    );
};

const CostEntryToolbar = ({ onClose }: { onClose: () => void }) => (
    <FormToolbar className="sticky top-0 z-50 p-4 border-b bg-background/95 backdrop-blur shadow-sm flex items-center justify-between mb-4 w-full">
        <span className="font-bold text-foreground text-base">Create Cost entry</span>
        <div className="flex gap-2 items-center">
            <Button type="button" variant="ghost" size="sm" className="cursor-pointer text-muted-foreground hover:text-foreground flex items-center gap-1.5" onClick={onClose}>
                <CircleX className="w-4 h-4" /> Cancel
            </Button>
            <SaveButton className="bg-primary hover:bg-primary/90 text-white" />
        </div>
    </FormToolbar>
);

const CostEntryForm = ({ projectId, onSuccess }: { projectId: string | number, onSuccess: () => void }) => {
    return (
        <CreateBase resource="cost_entries" mutationOptions={{ onSuccess }}>
            <SimpleForm
                className="space-y-4 px-6 pb-6 pt-2"
                toolbar={<CostEntryToolbar onClose={onSuccess} />}
            >
                <TextInput source="project_id" defaultValue={projectId} className="hidden" />
                <div className="flex flex-col gap-4">
                    <SelectInput source="category" choices={CATEGORIES} isRequired className="w-full" />
                    <DateInput source="entry_date" defaultValue={new Date().toISOString().split('T')[0]} isRequired className="w-full" />
                    <NumberInput source="amount" isRequired className="w-full" />
                    <TextInput source="reference" className="w-full" />
                    <CustomCategoryField />
                    <TextInput source="description" className="w-full" multiline />
                </div>
            </SimpleForm>
        </CreateBase>
    );
};

const inr = (v: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v);

export const ProjectCostEntries = ({ onTotalChange }: { onTotalChange?: (total: number) => void }) => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);

    const { data: costs } = useGetManyReference<CostEntry>(
        'cost_entries',
        { target: 'project_id', id: record?.id, pagination: { page: 1, perPage: 100 }, sort: { field: 'entry_date', order: 'DESC' } }
    );

    if (!record) return null;

    const totalCost = costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0;

    // Group costs by category (including custom_category label)
    const categoryTotals = costs?.reduce((acc: Record<string, { total: number; label: string }>, c) => {
        const key = c.category === 'custom' && c.custom_category ? c.custom_category : c.category;
        const label = c.category === 'custom' && c.custom_category
            ? c.custom_category
            : CATEGORIES.find(cat => cat.id === c.category)?.name ?? c.category;
        if (!acc[key]) acc[key] = { total: 0, label };
        acc[key].total += c.amount;
        return acc;
    }, {}) ?? {};

    // Notify parent of total for Budget vs Actual card
    onTotalChange?.(totalCost);

    return (
        <div className="space-y-6 pt-8 border-t border-border">
            <div className="flex justify-between items-center px-1">
                <h3 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-indigo-500" /> Cost Association
                </h3>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 cursor-pointer" onClick={() => setOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Cost Entry
                </Button>
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetContent className="sm:max-w-md p-0 overflow-y-auto [&>button]:hidden" side="right">
                        <CostEntryForm projectId={record.id} onSuccess={() => setOpen(false)} />
                    </SheetContent>
                </Sheet>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="pb-3 border-b border-border">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Cost Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-3">
                        {Object.entries(categoryTotals).map(([key, { total, label }]) => {
                            const pct = totalCost > 0 ? (total / totalCost) * 100 : 0;
                            return (
                                <div key={key} className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="font-medium text-foreground">{label}</span>
                                        <span className="font-bold">{inr(total)}</span>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-500 rounded-full transition-all"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                        {Object.keys(categoryTotals).length === 0 && (
                            <p className="text-sm text-muted-foreground italic text-center py-4">No cost entries yet</p>
                        )}
                        <div className="pt-4 mt-2 border-t border-border flex justify-between items-center font-bold text-lg">
                            <span>Total Applied Costs</span>
                            <span className="text-indigo-700">{inr(totalCost)}</span>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3 border-b border-border">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Recent Entries</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <ReferenceManyField reference="cost_entries" target="project_id">
                            <DataTable>
                                <DataTable.Col source="entry_date" label="Date" />
                                <DataTable.Col source="category" label="Category" />
                                <DataTable.Col source="custom_category" label="Custom" />
                                <DataTable.Col source="description" label="Description" />
                                <DataTable.Col source="amount" label="Amount" />
                            </DataTable>
                        </ReferenceManyField>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
